import React, { useCallback, useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { fetchTransactionById } from 'Util/_HumanWritten/FetchTransactionById';
import { resetReservationTimer } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import {
	onReceiveResetReservationEvent,
	removeResetReservationListener,
} from 'Events/HumanWritten/ResetReservationEvent';
import alertToast from 'Util/ToastifyUtils';
import { appConfigStore } from 'Models/AppConfigStore';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';

/**
 * How long before expiry the timer offers to extend the hold, at most.
 *
 * Capped at a third of the configured hold (`Reservation.holdMinutes`) so the offer stays a warning
 * rather than the default state: five minutes was fine against the old fixed 15-minute hold, but the
 * hold is tenant-configurable now, and on anything under 5 minutes a flat 300 would make the timer
 * render as the extend control from the moment it appeared.
 */
const MAX_EXPIRING_SOON_SECONDS = 300;

function getExpiringSoonSeconds(holdMinutes: number): number {
	return Math.min(MAX_EXPIRING_SOON_SECONDS, Math.floor((holdMinutes * 60) / 3));
}

/**
 * `card` is the compact two-line card — a small block that sits in a row of existing controls
 * (check-in renders it in the top nav). `strip` is a single full-width line for the purchase
 * modals, which have no spare room in their header and whose step indicator has to stay centred.
 *
 * `pill` and `banner` are the booking wizard's own pair, and they are the original pre-redesign
 * look rather than the compact one: the wizard renders the countdown *inside* its black back
 * button, where a light card reads as a foreign object stuck onto the button. `pill` is the web
 * form (a yellow time pill and a label on the button); `banner` is the full-width mobile bar under
 * the progress bar. Neither renders a nested <button> — see the note on the extend control below.
 */
export type ReservationTimerVariant = 'card' | 'strip' | 'pill' | 'banner';

interface ReservationTimerProps {
	transactionId: string;
	onExpiry: () => void;
	/** Extra class on the root, for a consumer that needs to place the timer in its own layout. */
	className?: string;
	variant?: ReservationTimerVariant;
	/**
	 * Hold off firing `onExpiry` while a payment is in flight. The countdown still shows; if it has
	 * reached zero, expiry fires as soon as this goes false again. Sending a payment to a reader extends
	 * the hold server-side, so a timer that refetches after the send rarely gets there (#2721).
	 */
	paused?: boolean;
}

function getSecondsUntilExpiry(expirationTime: Date | null): number | null {
	if (expirationTime == null) {
		return null;
	}
	const millisecondsToExpiry = expirationTime.getTime() - Date.now();
	if (millisecondsToExpiry > 0) {
		return Math.round(millisecondsToExpiry / 1000);
	}
	return 0;
}

export function ReservationTimer({
	transactionId,
	onExpiry,
	className,
	variant = 'card',
	paused = false,
}: ReservationTimerProps) {
	const [secondsUntilExpiry, setSecondsUntilExpiry] = useState<number | null>(null);
	const [clientsideExpiryTime, setClientsideExpiryTime] = useState<Date | null>(null);
	const [resetting, setResetting] = useState(false);
	const expiryHandledRef = useRef(false);

	// Fetch transaction to get server-side secondsUntilExpiry
	useEffect(() => {
		fetchTransactionById(transactionId)
			.then(result => {
				const transaction = result.data[0];
				// Use client clock + server's secondsUntilExpiry to avoid clock skew
				const expirationTime = moment(new Date()).add(transaction.secondsUntilExpiry, 'seconds').toDate();
				setClientsideExpiryTime(expirationTime);
				setSecondsUntilExpiry(getSecondsUntilExpiry(expirationTime));
			})
			.catch(() => {
				setSecondsUntilExpiry(null);
			});
	}, [transactionId]);

	// Listen for reset events (after timer extension)
	useEffect(() => {
		const listenerFunc = () => {
			fetchTransactionById(transactionId)
				.then(result => {
					const expirationTime = moment(new Date())
						.add(result.data[0].secondsUntilExpiry, 'seconds')
						.toDate();
					setClientsideExpiryTime(expirationTime);
					setSecondsUntilExpiry(getSecondsUntilExpiry(expirationTime));
				})
				.catch(() => {
					setSecondsUntilExpiry(null);
				});
		};
		onReceiveResetReservationEvent(listenerFunc);
		return () => removeResetReservationListener(listenerFunc);
	}, [transactionId]);

	// Countdown interval
	useEffect(() => {
		const intervalId = setInterval(() => {
			if (clientsideExpiryTime === null) return;
			setSecondsUntilExpiry(prev => {
				if (prev === null) return null;
				if (prev === 0) {
					clearInterval(intervalId);
					return 0;
				}
				return getSecondsUntilExpiry(clientsideExpiryTime);
			});
		}, 1000);
		return () => clearInterval(intervalId);
	}, [clientsideExpiryTime]);

	// Fires onExpiry at most once, from whichever route notices first: the countdown reaching zero,
	// or the server refusing to extend a hold that has already lapsed.
	const fireExpiry = useCallback(() => {
		if (expiryHandledRef.current) {
			return;
		}
		expiryHandledRef.current = true;
		onExpiry();
	}, [onExpiry]);

	// Handle expiry
	useEffect(() => {
		if (secondsUntilExpiry === 0 && !paused) {
			fireExpiry();
		}
	}, [secondsUntilExpiry, fireExpiry, paused]);

	if (secondsUntilExpiry === null) {
		return null;
	}

	const minutes = Math.floor(secondsUntilExpiry / 60);
	const seconds = secondsUntilExpiry % 60;

	// Near the end of the hold the card stops being a readout and becomes the extend control. The two
	// are the same state deliberately: there is no point telling someone their time is nearly up in a
	// place they cannot do anything about it.
	const extendable = secondsUntilExpiry <= getExpiringSoonSeconds(appConfigStore.reservationHoldMinutes)
		&& secondsUntilExpiry > 0;

	const handleResetTimer = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();

		// The pill/banner extend control is a <p> (it renders inside the wizard's back Button, and a
		// nested <button> would be invalid), so it cannot be `disabled` the way the card and strip
		// variants are. Guard here instead, or a double tap fires two extends.
		if (resetting) {
			return;
		}

		setResetting(true);
		resetReservationTimer(transactionId)
			.catch((err: any) => {
				// 403 is ExtendReservation reporting the hold already lapsed — there is nothing left
				// to extend, so this is the expiry, not a failed button press.
				if (err?.response?.status === 403) {
					fireExpiry();
					return;
				}
				alertToast('Could not extend timer', 'error');
			})
			.finally(() => setResetting(false));
	};

	const rootClassName = classNames('reservation-timer', `reservation-timer--${variant}`, className, {
		'reservation-timer--extendable': extendable,
	});

	// Zero padded so the countdown is always MM:SS and does not lose a character at 10:00 -> 9:59.
	const countdown = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

	const time = (
		<span className="reservation-timer__time">
			<Icon name="timer" useMaterialIcons />
			<span className="reservation-timer__count">
				{/*
					Each digit gets its own fixed 1ch slot. The body font is Be Vietnam Pro, whose figures
					are proportional and which ships no `tnum` feature, so `font-variant-numeric` cannot
					equalise them - a `1` is visibly narrower than an `8`. Pinning only the countdown's
					overall width stopped the card resizing but left the digits sliding around inside it,
					because a narrower string still centres differently. One slot per character is the only
					thing that holds every digit still.
				*/}
				{Array.from(countdown).map((character, index) => (
					// Positional by definition: the key is the slot, not the character in it, so an index
					// key is the correct one here rather than the usual mistake.
					// eslint-disable-next-line react/no-array-index-key
					<span
						key={index}
						className={
							character === ':' ? 'reservation-timer__separator' : 'reservation-timer__digit'
						}
					>
						{character}
					</span>
				))}
			</span>
		</span>
	);

	const extendLabel = resetting ? 'Extending...' : 'Extend timer';

	// The wizard mounts the countdown inside its back Button, so the extend control here has to be a
	// <p>, not a <button>: a button inside a button is invalid and swallows the outer click. That is
	// why this variant does not reuse the `strip`/`card` extend markup.
	if (variant === 'pill' || variant === 'banner') {
		return (
			<div
				className={rootClassName}
				onClick={e => {
					e.stopPropagation();
				}}
				role="presentation"
			>
				<div className="reservation-timer__timer-and-label">
					<div className="reservation-timer__pill">{time}</div>
					<p className="reservation-timer__label">Time remaining</p>
				</div>
				{extendable && (
					// Same wording as the card and strip variants — the control does the same thing in all
					// four, and "Reset timer" here against "Extend timer" elsewhere read as two features.
					<p className="reservation-timer__extend" onClick={handleResetTimer} role="presentation">
						{extendLabel}
					</p>
				)}
			</div>
		);
	}

	if (variant === 'strip') {
		return (
			<div className={rootClassName}>
				{time}
				<span className="reservation-timer__label">remaining</span>
				{extendable && (
					<button
						type="button"
						className="reservation-timer__extend"
						onClick={handleResetTimer}
						disabled={resetting}
					>
						{extendLabel}
					</button>
				)}
			</div>
		);
	}

	const cardContent = (
		<>
			{time}
			<span className="reservation-timer__label">{extendable ? extendLabel : 'Remaining'}</span>
		</>
	);

	if (!extendable) {
		return <div className={rootClassName}>{cardContent}</div>;
	}

	// The whole card is the control once it is extendable — a small link beside a countdown is a poor
	// target on the touch devices check-in runs on.
	return (
		<button type="button" className={rootClassName} onClick={handleResetTimer} disabled={resetting}>
			{cardContent}
		</button>
	);
}
